import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { LieferantenrechnungStatusLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Lieferantenrechnung } from 'api/lieferantenrechnung';
import { Timestamps } from 'formComponents/Timestamps';
import { LieferantenrechnungPosition } from 'api/lieferantenrechnungPosition';
import { textbausteineByKzNrSelector } from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { LieferantenrechnungPositionen } from './LieferantenrechnungPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface LieferantenrechnungItemProps {
    lieferantenrechnung: Lieferantenrechnung;
    lieferantenrechnungPositionen: LieferantenrechnungPosition[];
    withLieferant?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editLieferantenrechnung: () => void;
    // refetch: () => void;
}

export const LieferantenrechnungItem: React.FC<LieferantenrechnungItemProps> = ({
    lieferantenrechnung,
    lieferantenrechnungPositionen,
    withLieferant,
    viewMode,
    withHeader = true,
    editLieferantenrechnung,
}) => {
    const { lieferant } = lieferantenrechnung;

    const auth = useSelector(authSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const history = useHistory();

    if (!lieferant) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={lieferantenrechnung.anlageDat}
            createdDecimalTime={lieferantenrechnung.anlageUhr}
            createdUser={lieferantenrechnung.anlageUser}
            modifiedDate={lieferantenrechnung.updDat}
            modifiedDecimalTime={lieferantenrechnung.updUhr}
            modifiedUser={lieferantenrechnung.updUser}
        />
    );

    const renderStatus = () => (
        <div className={styles.status}>{LieferantenrechnungStatusLabel[lieferantenrechnung.status]}</div>
    );

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editLieferantenrechnung();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.rechnungInArbeit]: lieferantenrechnung.status === 0,
        [formStyles.rechnungVersendet]: lieferantenrechnung.status === 1 || lieferantenrechnung.status === 5,
        [formStyles.rechnungBezahlt]: lieferantenrechnung.status === 6,
    });

    const rechnungGutschrift = (lieferantenrechnung?.sNetto || 0) < 0 ? 'Gutschrift' : 'Lieferantenrechnung';
    const rechnungGutschriftShort = (lieferantenrechnung?.sNetto || 0) < 0 ? 'GS' : 'RN';

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>
                            {rechnungGutschriftShort} {lieferantenrechnung.nr}
                        </strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(lieferantenrechnung.datum)}</strong>
                    </div>
                    {withLieferant && (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + lieferant.nr);
                            }}
                        >
                            {`${lieferant.name1} ${lieferant.name2} (${lieferant.nr})`}
                        </div>
                    )}

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <LieferantenrechnungPositionen
                        lieferant={lieferant}
                        rNr={lieferantenrechnung.nr}
                        lieferantenrechnungPositionen={lieferantenrechnungPositionen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + lieferant.nr);
                                // openDetails();
                            }}
                        >
                            {rechnungGutschrift} {lieferantenrechnung.nr} -{' '}
                            {`${lieferant.name1} ${lieferant.name2} (${lieferant.nr})`}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {lieferant.plz} {lieferant.ort}, {lieferant.strasse}{' '}
                            {lieferant.land && `(${lieferant.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lief.Rechnung-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{lieferantenrechnung.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr (Lief.):</div>
                            <div className={formStyles.dataValue}>
                                <strong>{lieferantenrechnung.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat.:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(lieferantenrechnung.datum)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Eingangsdatum:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(lieferantenrechnung.datBis)}</strong>
                            </div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(rechnung.datBis)}</strong>
                            </div>
                        </div>
                        */}
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {lieferantenrechnung.zahlBed
                                    ? textbausteineByKzNr[12][lieferantenrechnung.zahlBed]
                                        ? textbausteineByKzNr[12][lieferantenrechnung.zahlBed].name ||
                                          lieferantenrechnung.zahlBed
                                        : lieferantenrechnung.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(lieferantenrechnung.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Lieferanten):</div>
                            <div className={formStyles.dataValue}>
                                {nl2br(lieferantenrechnung?.lieferant?.bemerkung)}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Lieferanten):</div>
                            <div className={formStyles.dataValue}>{nl2br(lieferantenrechnung?.lieferant?.liefBem)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <LieferantenrechnungPositionen
                        lieferant={lieferant}
                        rNr={lieferantenrechnung.nr}
                        lieferantenrechnungPositionen={lieferantenrechnungPositionen}
                        viewMode={viewMode}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(lieferantenrechnung.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>
                            Summe Brutto: {formatMoney(lieferantenrechnung.sBrutto)}
                        </div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(lieferantenrechnung.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
            {/* showCopy && (
                <KundeSelect
                    label="Kunde"
                    kNr={rechnung?.kNr}
                    name={kunde?.name1}
                    dialogOnly
                    onChange={async (newKNr) => {
                        if (rechnung && newKNr) {
                            await copyLieferantenrechnung(newKNr, rechnung, rechnungPositionen);
                            refetch();
                            setShowCopy(false);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            ) */}
        </>
    );
};
